// src/pages/Financas.js (Aprimorado com visualizações avançadas e análises)
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { FinanceContext } from '../store/financeContext';

// Componentes originais
import FinanceForm from '../components/Finance/FinanceForm';
import FinanceTable from '../components/Finance/FinanceTable';
import CashFlow from '../components/Finance/CashFlow';
import MonthlyGoal from '../components/Finance/MonthlyGoal';

// Componentes de visualização avançada
import {
  KpiCard,
  KpiCardGroup,
  MetricChart,
  DistributionPie,
  InsightGenerator,
  TrendLine,
  MetricTable
} from '../components/Visualization';

// Ícones
import { 
  TrendingUp, 
  Eye,
  Trash,
  DollarSign, 
  ArrowDown, 
  ArrowUp, 
  PieChart, 
  Target, 
  Filter,
  Plus,
  Calendar,
  CreditCard,
  FileText,
  SlidersHorizontal,
  Search,
  Download,
  Clock,
  BarChart2,
  AlertTriangle,
  Maximize2,
  ChevronRight,
  ChevronDown,
  Info,
  RefreshCw,
  HelpCircle
} from 'lucide-react';

// Função auxiliar para formatação segura
const formatCurrency = (value, decimals = 2) => {
  // Verifica se o valor é um número válido
  const numValue = Number(value);
  if (isNaN(numValue)) return 'R$ 0,00';
  return `R$ ${numValue.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

const formatPercent = (value, decimals = 1) => {
  const numValue = Number(value);
  if (isNaN(numValue)) return '0%';
  return `${numValue.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
};

const safeFormat = (value, decimals = 2) => {
  // Verifica se o valor é um número válido
  const numValue = Number(value);
  if (isNaN(numValue)) return '0.00';
  return numValue.toFixed(decimals);
};

const Financas = () => {
  // Estados
  const [currentView, setCurrentView] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedChart, setExpandedChart] = useState(null);
  const [showInsights, setShowInsights] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [activeTab, setActiveTab] = useState('income');
  const [showHelp, setShowHelp] = useState(false);
  
  // Contexto de finanças com valores padrão para evitar erros
  const financeContext = useContext(FinanceContext) || {};
  const {
    income = [],
    expenses = [],
    cashFlow = { income: 0, expenses: 0, profit: 0 },
    monthlyGoal = 0,
    nextMonthBudget = 0,
    goalAchieved = false,
    previousIncome = [],
    previousExpenses = [],
    previousCashFlow = { income: 0, expenses: 0, profit: 0 },
    addIncome = () => {},
    addExpense = () => {},
    removeIncome = () => {},
    removeExpense = () => {},
    updateMonthlyGoal = () => {},
    updateDistribution = () => {}
  } = financeContext;

  // Verificar se o contexto foi carregado corretamente
  useEffect(() => {
    if (financeContext) {
      setIsLoading(false);
    } else {
      setHasError(true);
      setIsLoading(false);
      console.error("Contexto financeiro não foi carregado corretamente.");
    }
  }, [financeContext]);
  
  // Manipuladores de eventos
  const handleAddIncome = (newIncome) => {
    addIncome({
      ...newIncome,
      id: Date.now().toString(),
      date: newIncome.date || new Date().toISOString().split('T')[0]
    });
  };

  const handleAddExpense = (newExpense) => {
    addExpense({
      ...newExpense,
      id: Date.now().toString(),
      date: newExpense.date || new Date().toISOString().split('T')[0]
    });
  };

  const handleRemoveItem = (id, type) => {
    if (type === 'income') removeIncome(id);
    else removeExpense(id);
  };

  const handleGoalUpdate = (value) => updateMonthlyGoal(value);
  const handleDistributionUpdate = (value) => updateDistribution(value);
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleExpandChart = (chartId) => {
    setExpandedChart(expandedChart === chartId ? null : chartId);
  };

  // Filtragem de itens financeiros
  const filteredIncome = useMemo(() => {
    let filtered = [...income];
    
    // Aplicar filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Aplicar filtro de categoria
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(item => item?.category === selectedCategory);
    }
    
    // Aplicar filtro de período
    if (timeRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (timeRange) {
        case 'week':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate = new Date(0); // Desde o início
      }
      
      filtered = filtered.filter(item => {
        if (!item.date) return true;
        const itemDate = new Date(item.date);
        return itemDate >= startDate;
      });
    }
    
    return filtered;
  }, [income, searchTerm, selectedCategory, timeRange]);
  
  const filteredExpenses = useMemo(() => {
    let filtered = [...expenses];
    
    // Aplicar filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Aplicar filtro de categoria
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(item => item?.category === selectedCategory);
    }
    
    // Aplicar filtro de período
    if (timeRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (timeRange) {
        case 'week':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate = new Date(0); // Desde o início
      }
      
      filtered = filtered.filter(item => {
        if (!item.date) return true;
        const itemDate = new Date(item.date);
        return itemDate >= startDate;
      });
    }
    
    return filtered;
  }, [expenses, searchTerm, selectedCategory, timeRange]);

  // Cálculos e análises
  const goalProgress = monthlyGoal > 0 
    ? ((cashFlow.profit || 0) / monthlyGoal) * 100 
    : 0;
  
  // Obter categorias de receitas
  const incomeCategories = useMemo(() => {
    const categories = {};
    
    filteredIncome.forEach(item => {
      if (!item || !item.category) return;
      categories[item.category] = (categories[item.category] || 0) + (item.amount || 0);
    });
    
    return Object.entries(categories)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredIncome]);
  
  // Obter categorias de despesas
  const expenseCategories = useMemo(() => {
    const categories = {};
    
    filteredExpenses.forEach(item => {
      if (!item || !item.category) return;
      categories[item.category] = (categories[item.category] || 0) + (item.amount || 0);
    });
    
    return Object.entries(categories)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredExpenses]);
  
  // Comparar com período anterior
  const incomeChange = previousCashFlow.income > 0 
    ? ((cashFlow.income - previousCashFlow.income) / previousCashFlow.income) * 100 
    : 0;
    
  const expensesChange = previousCashFlow.expenses > 0 
    ? ((cashFlow.expenses - previousCashFlow.expenses) / previousCashFlow.expenses) * 100 
    : 0;
    
  const profitChange = previousCashFlow.profit > 0 
    ? ((cashFlow.profit - previousCashFlow.profit) / previousCashFlow.profit) * 100 
    : 0;
  
  // Preparar dados para gráficos temporais
  const getMonthlyData = () => {
    // Gerar últimos 6 meses
    const monthsLabels = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now);
      month.setMonth(now.getMonth() - i);
      monthsLabels.push(month.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }));
    }
    
    // Dados fictícios para exemplo - em produção substituir por dados reais
    return monthsLabels.map((month, index) => ({
      month,
      receita: 10000 + (index * 1500) + (Math.random() * 3000),
      despesas: 7000 + (index * 800) + (Math.random() * 2000),
      lucro: 3000 + (index * 700) + (Math.random() * 1000)
    }));
  };
  
  const monthlyData = getMonthlyData();
  
  // Configuração do gráfico de fluxo de caixa
 const cashFlowChartConfig = {
  data: monthlyData,
  series: [
    { 
      type: 'bar', 
      dataKey: 'receita', 
      name: 'Receitas', 
      color: '#22C55E', 
      valueFormatter: val => formatCurrency(val)
    },
    { 
      type: 'bar', 
      dataKey: 'despesas', 
      name: 'Despesas', 
      color: '#EF4444', 
      valueFormatter: val => formatCurrency(val)
    },
    { 
      type: 'line', 
      dataKey: 'lucro', 
      name: 'Lucro', 
      color: '#3B82F6', 
      valueFormatter: val => formatCurrency(val),
      strokeWidth: 3
    }
  ],
  xAxisDataKey: 'month',
  title: 'Evolução Financeira',
  subtitle: 'Dados dos últimos 6 meses',
  height: 400,
  showControls: true,
  targets: [
    { 
      value: monthlyGoal, 
      label: `Meta Mensal: ${formatCurrency(monthlyGoal)}`, 
      color: '#F59E0B'
      // Removido yAxisId: 'right'
    }
  ]
};

  
  // Configuração da tabela de transações
  const financeTableColumns = [
    {
      key: 'date',
      label: 'Data',
      sortable: true,
      type: 'date',
      format: (value) => new Date(value).toLocaleDateString('pt-BR')
    },
    {
      key: 'description',
      label: 'Descrição',
      sortable: true
    },
    {
      key: 'category',
      label: 'Categoria',
      sortable: true,
      type: 'tag',
      tagColors: {
        'Vendas': 'bg-emerald-100 text-emerald-800',
        'Serviços': 'bg-blue-100 text-blue-800',
        'Investimentos': 'bg-purple-100 text-purple-800',
        'Operacional': 'bg-orange-100 text-orange-800',
        'Marketing': 'bg-pink-100 text-pink-800',
        'Pessoal': 'bg-indigo-100 text-indigo-800',
        'Impostos': 'bg-red-100 text-red-800'
      }
    },
    {
      key: 'amount',
      label: 'Valor',
      sortable: true,
      type: 'number',
      format: (value) => formatCurrency(value)
    }
  ];
  
  // Configuração para os KPIs
  const kpiMetrics = [
    { 
      title: 'Faturamento', 
      value: cashFlow.income, 
      prefix: 'R$ ', 
      change: parseFloat(incomeChange.toFixed(1)), 
      timeframe: 'vs. período anterior', 
      color: 'green', 
      inverseTrend: false,
      icon: <TrendingUp className="h-5 w-5 text-green-600" /> 
    },
    { 
      title: 'Despesas', 
      value: cashFlow.expenses, 
      prefix: 'R$ ', 
      change: parseFloat(expensesChange.toFixed(1)), 
      timeframe: 'vs. período anterior', 
      color: 'red', 
      inverseTrend: true,
      icon: <ArrowDown className="h-5 w-5 text-red-600" /> 
    },
    { 
      title: 'Lucro', 
      value: cashFlow.profit, 
      prefix: 'R$ ', 
      change: parseFloat(profitChange.toFixed(1)), 
      timeframe: 'vs. período anterior', 
      color: 'blue', 
      inverseTrend: false,
      icon: <DollarSign className="h-5 w-5 text-blue-600" /> 
    },
    { 
      title: 'Meta Mensal', 
      value: goalProgress, 
      suffix: '%', 
      change: 0, 
      timeframe: goalProgress >= 100 ? 'Meta atingida!' : `Faltam ${formatCurrency(monthlyGoal - cashFlow.profit > 0 ? monthlyGoal - cashFlow.profit : 0)}`, 
      color: 'yellow', 
      inverseTrend: false,
      icon: <Target className="h-5 w-5 text-yellow-600" /> 
    }
  ];
  
  // Verificar se há alerta de balanço negativo
  const hasNegativeBalance = cashFlow.profit < 0;
  
  // Estado para filtros de categoria
  const uniqueIncomeCategories = useMemo(() => {
    const categories = new Set();
    income.forEach(item => {
      if (item && item.category) {
        categories.add(item.category);
      }
    });
    return Array.from(categories);
  }, [income]);
  
  const uniqueExpenseCategories = useMemo(() => {
    const categories = new Set();
    expenses.forEach(item => {
      if (item && item.category) {
        categories.add(item.category);
      }
    });
    return Array.from(categories);
  }, [expenses]);
  
  // Cores para categorias
  const categoryColors = {
    'Vendas': '#10B981',
    'Serviços': '#3B82F6',
    'Investimentos': '#8B5CF6',
    'Operacional': '#F97316',
    'Marketing': '#EC4899',
    'Pessoal': '#6366F1',
    'Impostos': '#EF4444'
  };
  
  // Formatação de dados para o gráfico de distribuição
  const incomePieData = incomeCategories.map(cat => ({
    name: cat.name,
    value: cat.value
  }));
  
  const expensePieData = expenseCategories.map(cat => ({
    name: cat.name,
    value: cat.value
  }));
  
  // Loading state
  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados financeiros...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="page-container">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro ao carregar dados financeiros</h3>
              <p className="text-sm text-red-700 mt-1">
                Ocorreu um erro ao carregar os dados financeiros. Por favor, tente novamente mais tarde ou entre em contato com o suporte.
              </p>
              <button className="mt-3 btn-primary text-sm" onClick={() => window.location.reload()}>
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container space-y-6">
      {/* Modal para gráfico expandido */}
      {expandedChart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-bold text-xl">
                {expandedChart === 'cashflow' && 'Evolução Financeira Detalhada'}
                {expandedChart === 'income-dist' && 'Distribuição de Receitas Detalhada'}
                {expandedChart === 'expense-dist' && 'Distribuição de Despesas Detalhada'}
              </h3>
              <button 
                onClick={() => setExpandedChart(null)}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {expandedChart === 'cashflow' && (
                <div className="h-[600px]">
                  <MetricChart {...cashFlowChartConfig} height={550} />
                </div>
              )}
              {expandedChart === 'income-dist' && (
                <div className="h-[600px]">
                  <DistributionPie
                    data={incomePieData}
                    nameKey="name"
                    dataKey="value"
                    donut={true}
                    valueFormatter={(val) => formatCurrency(val)}
                    centerLabel="Total"
                    centerValue={formatCurrency(cashFlow.income)}
                    height={550}
                  />
                </div>
              )}
              {expandedChart === 'expense-dist' && (
                <div className="h-[600px]">
                  <DistributionPie
                    data={expensePieData}
                    nameKey="name"
                    dataKey="value"
                    donut={true}
                    valueFormatter={(val) => formatCurrency(val)}
                    centerLabel="Total"
                    centerValue={formatCurrency(cashFlow.expenses)}
                    height={550}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title mb-1">Controle Financeiro</h1>
          <p className="text-gray-600">
            Gerencie receitas, despesas e metas financeiras
            {goalAchieved && (
              <span className="ml-2 text-green-600 font-medium">
                Parabéns! Você atingiu {goalProgress.toFixed(0)}% da meta.
              </span>
            )}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Seletor de período */}
          <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-r border-gray-200">
              <Calendar size={18} className="text-gray-500" />
            </div>
            <select 
              className="py-2 px-3 text-sm text-gray-700 border-none focus:ring-0" 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="week">Últimos 7 dias</option>
              <option value="month">Últimos 30 dias</option>
              <option value="quarter">Último trimestre</option>
              <option value="year">Este ano</option>
              <option value="all">Todo o período</option>
            </select>
          </div>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            title="Atualizar dados"
          >
            <RefreshCw size={18} />
          </button>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            title="Exportar dados"
          >
            <Download size={18} />
          </button>
          
          <button 
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm text-gray-700" 
            onClick={() => setShowHelp(!showHelp)}
            title="Ajuda"
          >
            <HelpCircle size={18} />
          </button>
        </div>
      </div>
      
      {/* Mensagens de alerta/informação contextual */}
      {showHelp && (
        <div className="alert alert-info">
          <div className="alert-icon">
            <Info className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">Como usar este painel financeiro</h3>
            <p className="text-sm">Este painel apresenta uma visão completa das suas finanças. Use o seletor de período no topo para visualizar dados de diferentes intervalos de tempo. A guia "Visão Geral" mostra resumos e tendências, enquanto as guias "Receitas", "Despesas" e "Metas" permitem gerenciar cada aspecto específico.</p>
          </div>
        </div>
      )}
      
      {hasNegativeBalance && (
        <div className="alert alert-warning">
          <div className="alert-icon">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">Atenção: Balanço Negativo</h3>
            <p className="text-sm">Seu lucro está negativo no período selecionado. Considere revisar suas despesas ou estratégias para aumentar receitas.</p>
          </div>
        </div>
      )}

      {/* Navegação por Tabs */}
      <div className="flex overflow-x-auto">
        <nav className="tabs">
          {[
            { id: 'overview', label: 'Visão Geral', icon: <PieChart className="h-4 w-4 mr-2" /> },
            { id: 'income', label: 'Receitas', icon: <ArrowUp className="h-4 w-4 mr-2" /> },
            { id: 'expenses', label: 'Despesas', icon: <ArrowDown className="h-4 w-4 mr-2" /> },
            { id: 'goals', label: 'Metas', icon: <Target className="h-4 w-4 mr-2" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCurrentView(tab.id)}
              className={`tab flex items-center ${currentView === tab.id ? 'tab-active' : 'tab-inactive'}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Cards de métricas principais */}
      <KpiCardGroup metrics={kpiMetrics} layout="grid-medium" />

      {/* Visão Geral */}
      {currentView === 'overview' && (
        <div className="space-y-6">
          {/* Evolução Financeira */}
          <div className="card shadow-md">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
                Evolução Financeira
              </h2>
              <div className="flex items-center gap-2">
                <button 
                  className="p-1 rounded-lg hover:bg-gray-100 text-gray-600" 
                  onClick={() => toggleExpandChart('cashflow')}
                  title="Expandir gráfico"
                >
                  <Maximize2 size={18} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <MetricChart {...cashFlowChartConfig} />
            </div>
            <div className="px-6 pb-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Tendência de Receitas</p>
                  <div className="flex items-center justify-between">
                    <p className="text-blue-700 font-semibold">
                      {incomeChange >= 0 ? '+' : ''}{formatPercent(incomeChange)}
                    </p>
                    <span className={`text-xs ${incomeChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {incomeChange >= 0 ? 'Em alta' : 'Em queda'}
                    </span>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Tendência de Despesas</p>
                  <div className="flex items-center justify-between">
                    <p className="text-blue-700 font-semibold">
                      {expensesChange >= 0 ? '+' : ''}{formatPercent(expensesChange)}
                    </p>
                    <span className={`text-xs ${expensesChange <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {expensesChange <= 0 ? 'Em queda' : 'Em alta'}
                    </span>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Tendência de Lucro</p>
                  <div className="flex items-center justify-between">
                    <p className="text-blue-700 font-semibold">
                      {profitChange >= 0 ? '+' : ''}{formatPercent(profitChange)}
                    </p>
                    <span className={`text-xs ${profitChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {profitChange >= 0 ? 'Em alta' : 'Em queda'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Distribuição por Categoria */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Receitas por Categoria */}
            <div className="card shadow-md h-full">
              <div className="card-header">
                <h2 className="card-title flex items-center">
                  <ArrowUp className="h-5 w-5 text-green-600 mr-2" />
                  Receitas por Categoria
                </h2>
                <button 
                  className="p-1 rounded-lg hover:bg-gray-100 text-gray-600" 
                  onClick={() => toggleExpandChart('income-dist')}
                  title="Expandir gráfico"
                >
                  <Maximize2 size={18} />
                </button>
              </div>
              <div className="flex-1">
                <div className="p-6 h-64">
                  <DistributionPie
                    data={incomePieData}
                    nameKey="name"
                    dataKey="value"
                    donut={true}
                    valueFormatter={(val) => formatCurrency(val)}
                    centerLabel="Total"
                    centerValue={formatCurrency(cashFlow.income)}
                  />
                </div>
                <div className="px-6 pb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Principais categorias</h3>
                  <div className="space-y-2">
                    {incomePieData.slice(0, 3).map((category, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: categoryColors[category.name] || '#3B82F6' }}
                          ></div>
                          <span className="text-sm">{category.name}</span>
                        </div>
                        <span className="text-sm font-medium">{formatCurrency(category.value)}</span>
                      </div>
                    ))}
                    {incomePieData.length > 3 && (
                      <button 
                        className="text-xs text-blue-600 flex items-center"
                        onClick={() => setCurrentView('income')}
                      >
                        Ver todas <ChevronRight className="h-3 w-3 ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Despesas por Categoria */}
            <div className="card shadow-md h-full">
              <div className="card-header">
                <h2 className="card-title flex items-center">
                  <ArrowDown className="h-5 w-5 text-red-600 mr-2" />
                  Despesas por Categoria
                </h2>
                <button 
                  className="p-1 rounded-lg hover:bg-gray-100 text-gray-600" 
                  onClick={() => toggleExpandChart('expense-dist')}
                  title="Expandir gráfico"
                >
                  <Maximize2 size={18} />
                </button>
              </div>
              <div className="flex-1">
                <div className="p-6 h-64">
                  <DistributionPie
                    data={expensePieData}
                    nameKey="name"
                    dataKey="value"
                    donut={true}
                    valueFormatter={(val) => formatCurrency(val)}
                    centerLabel="Total"
                    centerValue={formatCurrency(cashFlow.expenses)}
                  />
                </div>
                <div className="px-6 pb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Principais categorias</h3>
                  <div className="space-y-2">
                    {expensePieData.slice(0, 3).map((category, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: categoryColors[category.name] || '#EF4444' }}
                          ></div>
                          <span className="text-sm">{category.name}</span>
                        </div>
                        <span className="text-sm font-medium">{formatCurrency(category.value)}</span>
                      </div>
                    ))}
                    {expensePieData.length > 3 && (
                      <button 
                        className="text-xs text-blue-600 flex items-center"
                        onClick={() => setCurrentView('expenses')}
                      >
                        Ver todas <ChevronRight className="h-3 w-3 ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Meta Mensal com Projeção */}
          <div className="card shadow-md">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <Target className="h-5 w-5 text-blue-600 mr-2" />
                Meta Mensal e Projeção
              </h2>
              <button 
                className="btn-outline text-sm flex items-center"
                onClick={() => setCurrentView('goals')}
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Ajustar Meta
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <div className="flex justify-between items-end mb-3">
                  <div>
                    <span className="text-sm text-gray-500">Progresso atual</span>
                    <h3 className="text-3xl font-bold">{safeFormat(goalProgress, 0)}%</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Meta</span>
                    <p className="text-xl font-semibold">{formatCurrency(monthlyGoal)}</p>
                  </div>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${goalAchieved ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(goalProgress, 100)}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Lucro Atual</p>
                  <p className={`text-xl font-bold ${(cashFlow.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(cashFlow.profit)}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 relative">
                  <div className="absolute top-2 right-2">
                    <div className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                      Projeção
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Lucro Projetado</p>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(cashFlow.profit * 1.15)} {/* Simulação de projeção */}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Próximo Mês</p>
                  <p className="text-xl font-bold">{formatCurrency(nextMonthBudget)}</p>
                </div>
              </div>
              
              <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2 flex items-center">
                  <Info className="h-4 w-4 mr-2" />
                  Recomendação
                </h3>
                <p className="text-sm text-blue-700">
                  {cashFlow.profit >= monthlyGoal
                    ? 'Você está acima da meta! Considere aumentar sua meta ou realocar o excedente para investimentos.'
                    : `Para atingir sua meta de ${formatCurrency(monthlyGoal)}, você precisa aumentar o lucro em ${formatCurrency(monthlyGoal - cashFlow.profit)} até o fim do mês.`
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Últimas Transações */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Últimas Receitas */}
            <div className="card shadow-md">
              <div className="card-header">
                <h2 className="card-title flex items-center">
                  <ArrowUp className="h-5 w-5 text-green-600 mr-2" />
                  Últimas Receitas
                </h2>
                <button 
                  className="btn-outline text-sm flex items-center"
                  onClick={() => setCurrentView('income')}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Ver Todas
                </button>
              </div>
              <div className="table-container">
                {filteredIncome.length > 0 ? (
                  <MetricTable
                    data={filteredIncome.slice(0, 5)}
                    columns={financeTableColumns}
                    enableSorting={true}
                    idField="id"
                    actions={[
                      {
                        label: 'Remover',
                        icon: <Trash className="h-4 w-4" />,
                        className: 'text-red-600 hover:bg-red-100',
                        onClick: (item) => handleRemoveItem(item.id, 'income')
                      }
                    ]}
                  />
                ) : (
                  <div className="p-8 text-center">
                    <ArrowUp className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">
                      Nenhuma receita cadastrada no período selecionado.
                    </p>
                    <button 
                      className="btn-primary flex items-center mx-auto"
                      onClick={() => setCurrentView('income')}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Receita
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Últimas Despesas */}
            <div className="card shadow-md">
              <div className="card-header">
                <h2 className="card-title flex items-center">
                  <ArrowDown className="h-5 w-5 text-red-600 mr-2" />
                  Últimas Despesas
                </h2>
                <button 
                  className="btn-outline text-sm flex items-center"
                  onClick={() => setCurrentView('expenses')}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Ver Todas
                </button>
              </div>
              <div className="table-container">
                {filteredExpenses.length > 0 ? (
                  <MetricTable
                    data={filteredExpenses.slice(0, 5)}
                    columns={financeTableColumns}
                    enableSorting={true}
                    idField="id"
                    actions={[
                      {
                        label: 'Remover',
                        icon: <Trash className="h-4 w-4" />,
                        className: 'text-red-600 hover:bg-red-100',
                        onClick: (item) => handleRemoveItem(item.id, 'expense')
                      }
                    ]}
                  />
                ) : (
                  <div className="p-8 text-center">
                    <ArrowDown className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">
                      Nenhuma despesa cadastrada no período selecionado.
                    </p>
                    <button 
                      className="btn-primary flex items-center mx-auto"
                      onClick={() => setCurrentView('expenses')}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Despesa
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Página de Receitas */}
      {currentView === 'income' && (
        <>
          <div className="card shadow-md mb-6">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <Plus className="h-5 w-5 text-blue-600 mr-2" />
                Adicionar Nova Receita
              </h2>
            </div>
            <div className="p-6">
              <FinanceForm type="income" onAddItem={handleAddIncome} />
            </div>
          </div>

          <div className="card shadow-md">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <FileText className="h-5 w-5 text-blue-600 mr-2" />
                Receitas Registradas
              </h2>
              <div className="flex space-x-3">
                <div className="relative hidden md:block">
                  <input
                    type="text"
                    placeholder="Buscar receitas..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
                <button 
                  className="btn-outline text-sm flex items-center"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filtrar
                </button>
                <button className="btn-outline text-sm flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar
                </button>
              </div>
            </div>
            
            {/* Buscador mobile */}
            <div className="md:hidden p-4 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar receitas..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            {/* Filtros (expandível) */}
            {filterOpen && (
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Categoria</label>
                    <select 
                      className="form-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">Todas as categorias</option>
                      {uniqueIncomeCategories.map((category, index) => (
                        <option key={index} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Data</label>
                    <select 
                      className="form-select"
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value)}
                    >
                      <option value="week">Esta semana</option>
                      <option value="month">Este mês</option>
                      <option value="quarter">Este trimestre</option>
                      <option value="year">Este ano</option>
                      <option value="all">Todo o período</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor</label>
                    <select className="form-select">
                      <option value="">Todos os valores</option>
                      <option value="low">Até R$ 1.000</option>
                      <option value="medium">R$ 1.000 a R$ 5.000</option>
                      <option value="high">Acima de R$ 5.000</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button 
                    className="btn-outline text-sm mr-3"
                    onClick={() => {
                      setSelectedCategory('all');
                      setTimeRange('month');
                      setSearchTerm('');
                    }}
                  >
                    Limpar Filtros
                  </button>
                  <button 
                    className="btn-primary text-sm"
                    onClick={() => setFilterOpen(false)}
                  >
                    Aplicar Filtros
                  </button>
                </div>
              </div>
            )}
            
            <div className="overflow-hidden">
              {filteredIncome.length > 0 ? (
                <MetricTable
                  data={filteredIncome}
                  columns={financeTableColumns}
                  enableSorting={true}
                  enableFiltering={false}
                  pagination={{ enabled: true, pageSize: 10 }}
                  idField="id"
                  title=""
                  actions={[
                    {
                      label: 'Remover',
                      icon: <Trash className="h-4 w-4" />,
                      className: 'text-red-600 hover:bg-red-100',
                      onClick: (item) => handleRemoveItem(item.id, 'income')
                    }
                  ]}
                />
              ) : (
                <div className="p-8 text-center">
                  <ArrowUp className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">
                    {searchTerm || selectedCategory !== 'all'
                      ? 'Nenhuma receita encontrada com os filtros atuais.'
                      : 'Nenhuma receita cadastrada ainda.'}
                  </p>
                  <button className="btn-primary flex items-center mx-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Receita
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Página de Despesas */}
      {currentView === 'expenses' && (
        <>
          <div className="card shadow-md mb-6">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <Plus className="h-5 w-5 text-blue-600 mr-2" />
                Adicionar Nova Despesa
              </h2>
            </div>
            <div className="p-6">
              <FinanceForm type="expense" onAddItem={handleAddExpense} />
            </div>
          </div>

          <div className="card shadow-md">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <FileText className="h-5 w-5 text-blue-600 mr-2" />
                Despesas Registradas
              </h2>
              <div className="flex space-x-3">
                <div className="relative hidden md:block">
                  <input
                    type="text"
                    placeholder="Buscar despesas..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
                <button 
                  className="btn-outline text-sm flex items-center"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filtrar
                </button>
                <button className="btn-outline text-sm flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar
                </button>
              </div>
            </div>
            
            {/* Buscador mobile */}
            <div className="md:hidden p-4 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar despesas..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            {/* Filtros (expandível) */}
            {filterOpen && (
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Categoria</label>
                    <select 
                      className="form-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">Todas as categorias</option>
                      {uniqueExpenseCategories.map((category, index) => (
                        <option key={index} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Data</label>
                    <select 
                      className="form-select"
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value)}
                    >
                      <option value="week">Esta semana</option>
                      <option value="month">Este mês</option>
                      <option value="quarter">Este trimestre</option>
                      <option value="year">Este ano</option>
                      <option value="all">Todo o período</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor</label>
                    <select className="form-select">
                      <option value="">Todos os valores</option>
                      <option value="low">Até R$ 1.000</option>
                      <option value="medium">R$ 1.000 a R$ 5.000</option>
                      <option value="high">Acima de R$ 5.000</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button 
                    className="btn-outline text-sm mr-3"
                    onClick={() => {
                      setSelectedCategory('all');
                      setTimeRange('month');
                      setSearchTerm('');
                    }}
                  >
                    Limpar Filtros
                  </button>
                  <button 
                    className="btn-primary text-sm"
                    onClick={() => setFilterOpen(false)}
                  >
                    Aplicar Filtros
                  </button>
                </div>
              </div>
            )}
            
            <div className="overflow-hidden">
              {filteredExpenses.length > 0 ? (
                <MetricTable
                  data={filteredExpenses}
                  columns={financeTableColumns}
                  enableSorting={true}
                  enableFiltering={false}
                  pagination={{ enabled: true, pageSize: 10 }}
                  idField="id"
                  title=""
                  actions={[
                    {
                      label: 'Remover',
                      icon: <Trash className="h-4 w-4" />,
                      className: 'text-red-600 hover:bg-red-100',
                      onClick: (item) => handleRemoveItem(item.id, 'expense')
                    }
                  ]}
                />
              ) : (
                <div className="p-8 text-center">
                  <ArrowDown className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">
                    {searchTerm || selectedCategory !== 'all'
                      ? 'Nenhuma despesa encontrada com os filtros atuais.'
                      : 'Nenhuma despesa cadastrada ainda.'}
                  </p>
                  <button className="btn-primary flex items-center mx-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Despesa
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Página de Metas */}
      {currentView === 'goals' && (
        <div className="card shadow-md">
          <div className="card-header">
            <h2 className="card-title flex items-center">
              <Target className="h-5 w-5 text-blue-600 mr-2" />
              Configuração de Metas
            </h2>
          </div>
          <div className="p-6">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-6">Progresso da Meta Atual</h3>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Meta Mensal</p>
                    <p className="text-3xl font-bold">{formatCurrency(monthlyGoal)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Lucro Atual</p>
                    <p className={`text-3xl font-bold ${(cashFlow.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(cashFlow.profit)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Progresso</p>
                    <p className={`text-3xl font-bold ${goalAchieved ? 'text-green-600' : 'text-blue-600'}`}>
                      {safeFormat(goalProgress, 0)}%
                    </p>
                  </div>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${goalAchieved ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(goalProgress, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <MonthlyGoal
              currentGoal={monthlyGoal}
              currentProfit={cashFlow.profit || 0}
              nextMonthBudget={nextMonthBudget}
              onGoalUpdate={handleGoalUpdate}
              onDistributionUpdate={handleDistributionUpdate}
            />
            
            <div className="mt-8 bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2 flex items-center">
                <Info className="h-4 w-4 mr-2" />
                Recomendações para Metas
              </h3>
              <div className="space-y-2 text-sm text-blue-700">
                <p>
                  <span className="font-medium">1. Metas realistas:</span> Estabeleça metas que sejam desafiadoras, mas alcançáveis. Comece com incrementos de 10-15% sobre seu lucro médio atual.
                </p>
                <p>
                  <span className="font-medium">2. Revisão periódica:</span> Avalie suas metas mensalmente e ajuste conforme necessário com base no desempenho real.
                </p>
                <p>
                  <span className="font-medium">3. Planejamento:</span> Se você está consistentemente superando suas metas, considere aumentá-las ou direcionar o excedente para investimentos futuros.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Financas;
